import { useState } from "react";
import { AlertTriangle, CalendarPlus, Clock, XCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { Modal } from "@/components/ui/Modal";
import { formatDate, formatDateTime } from "@/utils/formatDate";
import { apiErrorText } from "@/features/deliveries/utils/apiErrorText";
import { useExtendAcceptanceDeadline } from "../hooks/useOrders";
import type { Order } from "../types";
import {
  MAX_EXTENSION_DAYS_AHEAD,
  acceptanceDeadlineInfo,
  addDaysIso,
  minExtensionDate,
  todayIso,
} from "../utils/acceptanceDeadline";

/**
 * Pasek „czeka na przyjęcie do …” na karcie zamówienia GK (tylko status „Wysłane”):
 * termin przyjęcia, skąd się wziął i co dalej. Po terminie zamówienie dostaje flagę
 * „długo oczekuje” — nadal czeka na podwykonawcę, a o anulowaniu decyduje dyrektor
 * (`onCancel` — tylko dyrektor/CEO; otwiera okno „Anuluj jako bezprzedmiotowe”).
 * Zamawiający (`canExtend` — backend: CanManageOrderObject) może przedłużyć termin.
 */
export function AcceptanceDeadlineNotice({
  order,
  canExtend,
  onCancel,
}: {
  order: Order;
  canExtend: boolean;
  onCancel?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [newDeadline, setNewDeadline] = useState("");
  const [reason, setReason] = useState("");
  const extend = useExtendAcceptanceDeadline();

  const today = todayIso();
  const info = acceptanceDeadlineInfo(order, today);
  if (!info) return null;

  const flagged = Boolean(info.overdueSince);
  const warn = flagged || info.urgency !== "normal";
  const minDate = minExtensionDate(info.deadline, today);
  const maxDate = addDaysIso(today, MAX_EXTENSION_DAYS_AHEAD);

  const openModal = () => {
    setNewDeadline(minDate);
    setReason("");
    extend.reset();
    setOpen(true);
  };

  const submit = () =>
    extend.mutate(
      { id: order.id, confirmation_deadline: newDeadline, reason: reason.trim() },
      { onSuccess: () => setOpen(false) },
    );

  return (
    <>
      <div
        role="status"
        data-testid="acceptance-deadline-notice"
        data-urgency={info.urgency}
        data-overdue-flag={flagged ? "true" : "false"}
        className={`mt-3 flex flex-wrap items-start justify-between gap-2 rounded border px-3 py-2 text-xs ${
          warn
            ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
            : "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
        }`}
      >
        <div className="flex items-start gap-2 min-w-0">
          {warn ? <AlertTriangle size={14} className="mt-0.5 shrink-0" /> : <Clock size={14} className="mt-0.5 shrink-0" />}
          <div>
            {flagged ? (
              <>
                <p>
                  <strong>Długo oczekuje na przyjęcie</strong> — termin przyjęcia minął{" "}
                  <strong>{formatDate(info.deadline)}</strong>
                  {info.termDetail && <span> ({info.termDetail})</span>}
                  {info.overdueSince && <span>; oznaczone {formatDateTime(info.overdueSince)}</span>}.
                </p>
                <p className="mt-0.5">
                  Zamówienie nadal czeka — podwykonawca wciąż może je przyjąć. O anulowaniu decyduje dyrektor.
                </p>
              </>
            ) : (
              <>
                <p>
                  Czeka na przyjęcie przez podwykonawcę — termin przyjęcia:{" "}
                  <strong>{formatDate(info.deadline)}</strong>
                  {info.termDetail && <span> ({info.termDetail})</span>}
                </p>
                {info.tracked ? (
                  <p className="mt-0.5">
                    {info.urgency === "overdue"
                      ? "Termin minął — przy najbliższym przebiegu (codziennie o 7:00) zamówienie trafi do dyrekcji jako długo oczekujące, chyba że podwykonawca je przyjmie albo termin zostanie przedłużony."
                      : `Jeśli podwykonawca nie przyjmie zamówienia do tego dnia, od ${formatDate(info.overdueOn)} trafi ono do dyrekcji jako długo oczekujące.`}
                  </p>
                ) : (
                  <p className="mt-0.5">Termin tego zamówienia nie jest pilnowany automatycznie.</p>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          {canExtend && (
            <button
              type="button"
              onClick={openModal}
              className="flex items-center gap-1 font-medium text-[var(--color-primary)] hover:underline"
            >
              <CalendarPlus size={13} /> Przedłuż termin
            </button>
          )}
          {flagged && onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="flex items-center gap-1 font-medium text-red-600 dark:text-red-400 hover:underline"
            >
              <XCircle size={13} /> Anuluj zamówienie
            </button>
          )}
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Przedłuż termin przyjęcia">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Obecny termin przyjęcia: <strong>{formatDate(info.deadline)}</strong>. Wybierz nowy ostatni
            dzień, w którym podwykonawca może przyjąć zamówienie (najdalej {MAX_EXTENSION_DAYS_AHEAD} dni
            od dziś). Flaga „długo oczekuje” zniknie, a zgłaszający dostanie ponownie monit przed nowym
            terminem.
          </p>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1" htmlFor="acceptance-deadline-new">
              Nowy termin przyjęcia
            </label>
            <DatePicker
              id="acceptance-deadline-new"
              value={newDeadline}
              onChange={setNewDeadline}
              min={minDate}
              max={maxDate}
              clearable={false}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1" htmlFor="acceptance-deadline-reason">
              Powód (opcjonalnie)
            </label>
            <textarea
              id="acceptance-deadline-reason"
              rows={2}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="np. Podwykonawca na urlopie do piątku — potwierdził telefonicznie"
            />
          </div>
          {extend.isError && (
            <p className="text-sm text-red-600">
              {apiErrorText(extend.error, "Nie udało się przedłużyć terminu. Spróbuj ponownie.")}
            </p>
          )}
          <div className="flex gap-3">
            <Button loading={extend.isPending} disabled={!newDeadline} onClick={submit}>
              Przedłuż termin
            </Button>
            <Button variant="secondary" onClick={() => setOpen(false)}>Anuluj</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
